import { type FC, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Box,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  useCurrentPlatformQuery,
  useUpdateCurrentPlatformMutation,
} from "@/partner/entities/platforms";
import { FormBuilder } from "@/shared/components/Form";
import { ErrorMessage } from "@/shared/components";
import {
  makePlatformBrandingSchema,
  mapPlatformToBrandingFormValues,
} from "../model";

export const PlatformBranding: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { data, loading, error, refetch } = useCurrentPlatformQuery();

  const [updateCurrentPlatform, { loading: updating }] =
    useUpdateCurrentPlatformMutation({
      onError: (error) => {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      },
      onCompleted: () => {
        enqueueSnackbar(t("platformBranding.snackbar.updated"), {
          variant: "success",
        });
        refetch();
      },
    });

  const schema = useMemo(() => makePlatformBrandingSchema(t), [t]);

  const initialValues = useMemo(
    () => mapPlatformToBrandingFormValues(data?.currentPlatform),
    [data]
  );

  const handleSubmit = async (values: Record<string, any>) => {
    await updateCurrentPlatform({
      variables: {
        input: values,
      },
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <ErrorMessage error={error} />;
  }

  if (!data?.currentPlatform) {
    return (
      <Card>
        <CardContent>
          <ErrorMessage error={new Error(t("platform.errors.noPlatform"))} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title={t("platformBranding.card.title")}
        subheader={t("platformBranding.card.subheader")}
        sx={{ px: 2, pt: 2, pb: 0 }}
      />
      <CardContent sx={{ px: 2, pb: 2, pt: 2 }}>
        <FormBuilder
          formId="platform-branding-form"
          schema={schema}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          submitButton
          resetButton={false}
          submitButtonText={t("common.actions.save")}
          disabled={updating}
          spacing={3}
        />
      </CardContent>
    </Card>
  );
};
